import React, { useEffect, useState } from "react";
import AccountLayout from "../../account/AccountLayout";
import { useAccount } from "../../account/AccountProvider";
import { fetchUsage } from "../../account/accountApiClient";
import { AccountButton, AccountCard, AccountStatus } from "../../account/AccountUi";
import { DISPLAY_PLANS } from "../../billing/planCatalog";
import { captureScope, isCurrentScope } from "../../auth/authSession";
import { formatDate } from "../../account/entitlementAdapter";

const GROUPS = [
  {
    id: "bot",
    title: "Bot Trader",
    metrics: ["activePaperBots", "activeCompanyEventRules"],
    benefitKeys: ["paperBotTimeframes", "eventPushAllowed"],
  },
  {
    id: "building",
    title: "Building Strategies",
    metrics: ["extraSavedStrategies", "watchlistSymbols", "optimizerVariantsPerRun"],
    benefitKeys: ["optimizerTimeframes"],
  },
  {
    id: "discovery",
    title: "Strategy Discovery",
    metrics: ["discoveryRunsPerUtcDay"],
    benefitKeys: ["discoveryTimeframes", "discoveryLevel"],
  },
];

const METRIC_LABELS = {
  activePaperBots: "Active Cloud Paper Bots",
  activeCompanyEventRules: "Active company event rules",
  extraSavedStrategies: "Extra saved strategies",
  watchlistSymbols: "Watchlist symbols",
  optimizerVariantsPerRun: "Optimizer variants per run",
  discoveryRunsPerUtcDay: "Discovery runs per UTC day",
};

function formatMetric(metric) {
  if (!metric || metric.status === "unavailable") return "Unavailable";
  if (metric.kind === "per_run_cap") {
    return metric.effectiveLimit == null ? "Unavailable" : `Up to ${metric.effectiveLimit} per run`;
  }
  const used = metric.used == null ? "—" : metric.used;
  const limit = metric.effectiveLimit == null ? "—" : metric.effectiveLimit;
  const remaining = metric.remaining == null ? null : metric.remaining;
  return remaining == null ? `${used} / ${limit}` : `${used} used · ${remaining} remaining · limit ${limit}`;
}

export default function UsageBenefitsPage() {
  return (
    <AccountLayout
      title="Usage & benefits"
      description="How much of your plan you are using, grouped by product."
      path="/account/usage"
    >
      <UsageBenefitsBody />
    </AccountLayout>
  );
}

function UsageBenefitsBody() {
  const { entitlement } = useAccount();
  const [usage, setUsage] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);

  async function load() {
    const scope = captureScope();
    setState("loading");
    setError(null);
    try {
      const json = await fetchUsage({ scope });
      if (!isCurrentScope(scope)) return;
      setUsage(json.usage);
      setState("ready");
    } catch (err) {
      if (!isCurrentScope(scope)) return;
      setError(err);
      setState("error");
    }
  }

  useEffect(() => {
    load();
  }, []);

  const planId = entitlement?.freshness === "ready" ? entitlement.plan : null;
  const catalog = planId ? DISPLAY_PLANS[planId] : null;
  const limits = entitlement?.effectiveLimits || catalog?.limits || null;

  return (
      <div className="space-y-5">
        {state === "loading" && !usage ? <AccountStatus>Loading usage…</AccountStatus> : null}
        {state === "error" ? (
          <AccountCard>
            <AccountStatus tone="error">{error?.message || "We couldn’t load usage."}</AccountStatus>
            <AccountButton variant="secondary" className="mt-3" onClick={load}>
              Try again
            </AccountButton>
          </AccountCard>
        ) : null}
        {usage?.status === "degraded" ? (
          <AccountStatus>Some usage counters could not be confirmed. Unavailable is not the same as zero.</AccountStatus>
        ) : null}

        {GROUPS.map((group) => (
          <AccountCard key={group.id}>
            <h2 className="text-base font-semibold text-ink">{group.title}</h2>
            <ul className="mt-3 space-y-2 text-sm text-ink/80">
              {group.metrics.map((key) => {
                const metric = usage?.metrics?.[key];
                const fallbackLimit = limits?.[key];
                return (
                  <li key={key}>
                    <span className="font-medium text-ink">{METRIC_LABELS[key] || key}</span>
                    <span className="mt-0.5 block text-muted">
                      {metric ? formatMetric(metric) : fallbackLimit == null ? "Unavailable" : `Limit ${fallbackLimit} · usage not loaded`}
                    </span>
                    {metric?.resetsAt ? (
                      <span className="block text-xs text-muted">Resets {formatDate(metric.resetsAt)}</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {catalog ? (
              <div className="mt-4 text-sm text-muted">
                {group.id === "bot" ? (
                  <p>Paper-bot timeframes: {(catalog.paperBotTimeframes || []).join(", ") || "—"}</p>
                ) : null}
                {group.id === "building" ? (
                  <p>Optimizer timeframes: {(catalog.optimizerTimeframes || []).join(", ") || "—"}</p>
                ) : null}
                {group.id === "discovery" ? (
                  <p>
                    Discovery level: {catalog.discoveryLevel || "—"}
                    {catalog.discoveryTimeframes?.length ? ` · ${catalog.discoveryTimeframes.join(", ")}` : ""}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted">Benefits are shown after your plan is confirmed.</p>
            )}
          </AccountCard>
        ))}
      </div>
  );
}
